import { useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import FlagIcon from "@mui/icons-material/Flag";
import TranslateIcon from "@mui/icons-material/Translate";
import TuneIcon from "@mui/icons-material/Tune";
import BarChartIcon from "@mui/icons-material/BarChart";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";

export function Home() {
  const { t } = useTranslation();
  const { isAuthenticated, loading, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const features = [
    {
      icon: <FlagIcon sx={{ fontSize: 36, color: "#EF5350" }} />,
      title: t("home.feature1Title"),
      desc: t("home.feature1Desc"),
    },
    {
      icon: <TranslateIcon sx={{ fontSize: 36, color: "#7C4DFF" }} />,
      title: t("home.feature2Title"),
      desc: t("home.feature2Desc"),
    },
    {
      icon: <TuneIcon sx={{ fontSize: 36, color: "#00BCD4" }} />,
      title: t("home.feature3Title"),
      desc: t("home.feature3Desc"),
    },
    {
      icon: <BarChartIcon sx={{ fontSize: 36, color: "#4CAF50" }} />,
      title: t("home.feature4Title"),
      desc: t("home.feature4Desc"),
    },
    {
      icon: <DarkModeIcon sx={{ fontSize: 36, color: "#FF9800" }} />,
      title: t("home.feature5Title"),
      desc: t("home.feature5Desc"),
    },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ textAlign: "center", py: { xs: 6, md: 12 } }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            mb: 1,
            lineHeight: 1.1,
          }}
        >
          {t("home.hero")}
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            mb: 3,
            color: "primary.main",
            lineHeight: 1.1,
          }}
        >
          {t("home.heroHighlight")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 5,
            maxWidth: 600,
            mx: "auto",
            lineHeight: 1.7,
          }}
        >
          {t("home.heroSubtitle")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={loginWithGoogle}
            sx={{ px: 4 }}
          >
            {t("home.loginWithGoogle")}
          </Button>
          <Button
            variant="outlined"
            size="large"
            component={RouterLink}
            to="/preview"
            sx={{ px: 4 }}
          >
            {t("preview.cta")}
          </Button>
        </Box>
      </Box>
      <Box sx={{ py: { xs: 4, md: 8 }, textAlign: "center" }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
        >
          {t("home.featuresTitle")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 6,
            maxWidth: 600,
            mx: "auto",
          }}
        >
          {t("home.featuresSubtitle")}
        </Typography>
        <Grid container spacing={3}>
          {features.map((f, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: idx < 3 ? 4 : 6 }} key={idx}>
              <Card sx={{ height: "100%", textAlign: "left" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 2 }}>{f.icon}</Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {f.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {f.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
}
